import { AsyncPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { Duration } from 'dayjs/esm/plugin/duration';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  skipWhile,
  Subscription,
  switchMap,
  takeUntil
} from 'rxjs';
import { Bildungsangebot, Termin } from '../../../model';
import { getDauerKurzbezeichnungWithTimeRefence } from '../../../model/dauer';

import { RouterLink } from '@angular/router';
import { take } from 'rxjs/operators';
import { LIB_CONFIG } from '../../../lib.config';
import {
  getKostenKurzbezeichnungWithBoundary,
  Kosten,
  KostenBezeichnungWithBoundary
} from '../../../model/kosten';

import {
  SortableColumn,
  SortDirection,
  TerminTabelleSortingService
} from '../../../services/state/termin-tabelle-sorting.service';
import { getDuration } from '../../../shared/utils/duration';
import { DauerAnzeigeComponent } from '../../dauer-anzeige/dauer-anzeige.component';
import {
  MobileColumnSortComponent,
  SortColDropdownItem
} from '../mobile-column-sort/mobile-column-sort.component';
import { TerminTabellePaginierungComponent } from '../termin-tabelle-paginierung/termin-tabelle-paginierung.component';
import { CleanupService } from '../../../shared/utils/cleanup.service';
import { ScreenSizeService, ViewMode } from '../../../services/screen-size';
import { BildungsangeboteService } from '../../../services/bildungsangebote.service';
import { LoaderService } from '../../../services/loader.service';

type TerminWithExtendedData = Termin & {
  duration: Duration | number;
  kosten: KostenBezeichnungWithBoundary;
};

@Component({
  selector: 'now-wbsuche-lib-termin-tabelle-kachel',
  templateUrl: './termin-tabelle-kachel.component.html',
  styleUrls: ['./termin-tabelle-kachel.component.scss'],
  imports: [
    DatePipe,
    AsyncPipe,
    RouterLink,
    TerminTabellePaginierungComponent,
    DauerAnzeigeComponent,
    NgTemplateOutlet,
    MobileColumnSortComponent
  ],
  providers: [CleanupService]
})
export class TerminTabelleKachelComponent {
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly bildungsangeboteService = inject(BildungsangeboteService);
  private readonly sortingService = inject(TerminTabelleSortingService);
  private readonly cleanupService = inject(CleanupService);
  private readonly loaderService = inject(LoaderService);

  anzahlTermine: number = 0;

  private sub?: Subscription;

  @Input()
  set angebot(angebot: Bildungsangebot) {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.currentPage$.getValue() != 1) {
      this.currentPage$.next(1);
    }
    this._termine$.next(angebot.termine);
    this.anzahlTermine = angebot.anzahlTermine;
    this.sub = this.createSub(angebot.id);
  }

  @Output()
  public terminSelected = new EventEmitter<string>();

  @ViewChild('heading') private readonly heading!: ElementRef<HTMLElement>;

  public SortableColumn = SortableColumn;
  public readonly KEINE_ANGABE = 'Keine Angabe';
  public readonly REGELMAESSIGER_START = 'Regelmäßiger Start';
  private readonly _termine$: BehaviorSubject<Termin[]> = new BehaviorSubject<Termin[]>([]);
  public termineWithDuration$: Observable<TerminWithExtendedData[]> = this._termine$.pipe(
    map((termine) =>
      termine.map((termin) => {
        let duration: Duration | number = 0;
        if (termin?.beginn && termin?.ende) {
          duration = getDuration(termin.beginn, termin.ende);
        } else if (termin.dauer) {
          duration = getDauerKurzbezeichnungWithTimeRefence(termin.dauer.id).duration;
        }
        const kosten = getKostenKurzbezeichnungWithBoundary(termin.kostenWert);

        return {
          ...termin,
          duration,
          kosten: kosten
        };
      })
    )
  );

  public isDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.DESKTOP);
  public isPhone$ = this.screenSizeService.rangeTo$(ViewMode.PHONE);
  public currentPage$ = new BehaviorSubject<number>(1);
  public readonly pageSize = 5;
  public sortColumn$ = this.sortingService.sortColumn$;
  public sortDirection$ = this.sortingService.sortDirection$;
  public termineLoading$ = this.loaderService.angebotTermineLoading$;
  public isNowWbSuche = inject(LIB_CONFIG).isNowWbSuche;

  private createSub(id: number): Subscription {
    return combineLatest([this.sortColumn$, this.sortDirection$, this.currentPage$])
      .pipe(
        takeUntil(this.cleanupService.destroyed$),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) == JSON.stringify(curr)),
        //distinctUntilChanged lässt den initial Wert durch. Dieser wird skipped, da die ersten termine bereits bei
        //der Angebotsanfrage mit geholt werden
        skipWhile(
          ([column, direction], i) =>
            i == 0 && column == SortableColumn.BEGINN && direction == SortDirection.ASCENDING
        ),
        switchMap(([column, direction, currentPage]) =>
          this.bildungsangeboteService
            .fetchTermineForAngebot(id, currentPage, column, direction)
            .pipe(take(1))
        )
      )
      .subscribe((termine) => {
        if (termine != null) {
          this._termine$.next(termine);
        }
      });
  }

  public async currentPageChange(page: number): Promise<void> {
    this.currentPage$.next(page);
    const isPhone = await firstValueFrom(this.isPhone$);
    if (isPhone) {
      this.heading.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  public sortChanged(column: SortableColumn): void {
    if (this.sortingService.getSortColumn() !== column) {
      this.sortingService.setSortDirection(SortDirection.ASCENDING);
    } else {
      this.sortingService.setSortDirection(
        this.sortingService.getSortDirection() === SortDirection.DESCENDING
          ? SortDirection.ASCENDING
          : SortDirection.DESCENDING
      );
    }
    this.sortingService.setSortColumn(column);
  }

  public sortChangedFromDropdown(item: SortColDropdownItem): void {
    this.sortingService.setSortDirection(item.value.direction);
    this.sortingService.setSortColumn(item.value.col);
  }

  public getSortierungForColumn(column: SortableColumn): SortDirection | null {
    return this.sortingService.getSortColumn() === column
      ? this.sortingService.getSortDirection()
      : null;
  }

  selectTermin($event: MouseEvent, termin: Termin) {
    $event.preventDefault();
    $event.stopPropagation();
    if (termin?.quelle?.detailsLink !== undefined) {
      window.open(termin.quelle.detailsLink, '_blank');
    } else {
      this.terminSelected.emit(termin.id);
    }
  }

  protected readonly Kosten = Kosten;
}
